import { Button, Div, Label, Loader, PageSlider, Slider, Touchable } from "@grassrootly/rn-core";
import { RootStackScreenProps } from "../../navigation/Navigation.types";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import React from "react";
import GameSectionVote from "./GameSectionVote";
import { useGameVote } from "./useGameVote";
import { VoteType } from "@grassrootly/api";
import { Platform } from "react-native";
import GameVoteOrganizationScreen from "./GameVoteOrganizationScreen";
import GameVoteTrainingScreen from "./GameVoteTrainingScreen";
import { useGameView } from "../../api/game/GameViewSelector";

export default function GameVoteScreen({ route, navigation }: RootStackScreenProps<"vote">) {
  const gameView = useGameView(route.params.id);

  // Route to ORGANIZATION_MATCH voting screen
  if (gameView?.game_type === 'ORGANIZATION_MATCH') {
    return <GameVoteOrganizationScreen route={route} navigation={navigation} />;
  }

  // Route to TRAINING voting screen with opponent/teammate split
  if (gameView?.game_type === 'TRAINING') {
    return <GameVoteTrainingScreen route={route} navigation={navigation} />;
  }

  // Continue with existing COMPETITIVE voting screen (unchanged)
  const insents = useSafeAreaInsets();
  const { game, submitVotes, saving, goBack } = useGameVote(route.params.id, navigation);
  const [index, setIndex] = React.useState(0);
  const [creativity, setcreativity] = React.useState<number | null>(null);
  const [defenseMaster, setDefenseMaster] = React.useState<number | null>(null);
  const [amazingEffort, setAmazingEffort] = React.useState<number | null>(null);

  const pageRef = React.useRef<Slider>(null);

  const areVotesMissing = () => {
    return !creativity || !defenseMaster || !amazingEffort;
  };
  return (
    <Div
      classNames={[
        "flex1",
        "bgNeutralDarkerDark",
        "bwTop-{1}",
        "borderPrimary",
        "p16",
        `pb-{${insents.bottom + 16 + (Platform.OS == "web" ? 16 : 0)}}`,
        "h100",
      ]}
    >
      {!game ? (
        <Loader size={"large"} />
      ) : (
        <Div classNames={["flex1", "maxW-{920}", "w-{100%}", "mr-{auto}", "ml-{auto}"]}>
          <Touchable classNames={["ml-{auto}"]} onPress={goBack}>
            <Label type="l2Regular" classNames={["colorPrimaryMain"]}>
              Later
            </Label>
          </Touchable>
          <Div classNames={["flex1", "mt-{24}"]}>
            <Label type="l1Regular" classNames={["textCenter"]}>
              The Game has finished.
            </Label>
            <Label type="l1Regular" classNames={["textCenter", "mb-{36}"]}>
              Let's see who was the best player!
            </Label>
            <PageSlider
              classNames={["flex1", "mb-{24}"]}
              initialPageIndex={index}
              onPageChange={setIndex}
              slider={pageRef}
              slides={[
                <GameSectionVote
                  title="Who was the best Playmaker?"
                  attendees={game.attendees}
                  onSelected={setcreativity}
                  selectedId={creativity}
                  hostTeamName={game.host_team_name}
                  hostTeamLogo={game.host_team_logo}
                  visitorTeamName={game.visitor_team_name}
                  visitorTeamLogo={game.visitor_team_logo}
                />,
                <GameSectionVote
                  title="Who was the best Defender?"
                  attendees={game.attendees}
                  onSelected={setDefenseMaster}
                  selectedId={defenseMaster}
                  hostTeamName={game.host_team_name}
                  hostTeamLogo={game.host_team_logo}
                  visitorTeamName={game.visitor_team_name}
                  visitorTeamLogo={game.visitor_team_logo}
                />,
                <GameSectionVote
                  title="Who was the Workhorse?"
                  attendees={game.attendees}
                  onSelected={setAmazingEffort}
                  selectedId={amazingEffort}
                  hostTeamName={game.host_team_name}
                  hostTeamLogo={game.host_team_logo}
                  visitorTeamName={game.visitor_team_name}
                  visitorTeamLogo={game.visitor_team_logo}
                />,
              ]}
            />
            <Div classNames={["flexRow", "alignItemsCenter", "justifyContentCenter", "mv-{8}", "mt-{auto}", "mb-{24}"]}>
              {[0, 1, 2].map((indicator, i) => (
                <Touchable
                  onPress={() => pageRef.current?.setPage(i)}
                  key={i}
                  classNames={[
                    "w-{13}",
                    "h-{3}",
                    "p-{2}",
                    "br-{3}",
                    "mh-{5}",
                    `opacity-{${index == i ? "1" : "0.2"}}`,
                    index == i ? "bgPrimaryMain" : "bgColor-{lightgray}",
                  ]}
                />
              ))}
            </Div>
            {!areVotesMissing() ? (
              <Button
                title="Submit Votes"
                classNames={["btn", "btnPrimary", "btnBlock"]}
                isLoading={saving}
                disabled={saving}
                onPress={() => {
                  if (!creativity || !defenseMaster || !amazingEffort) return;
                  submitVotes([
                    { type: VoteType.CREATIVITY, attendanceId: creativity },
                    { type: VoteType.DEFENSE, attendanceId: defenseMaster },
                    { type: VoteType.EFFORT, attendanceId: amazingEffort },
                  ]);
                }}
              />
            ) : (
              <Button
                title="Next"
                classNames={[
                  "btn", 
                  "btnPrimary", 
                  "btnBlock", 
                  (index > 1 || (index === 0 && !creativity) || (index === 1 && !defenseMaster)) ? "opacity-{0.5}" : ""
                ]}
                onPress={() => pageRef.current?.setPage(index + 1)}
                disabled={index > 1 || (index === 0 && !creativity) || (index === 1 && !defenseMaster)}
              />
            )}
          </Div>
        </Div>
      )}
    </Div>
  );
}
